import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ImageOff, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { commsFilesAPI, fmtFull } from '../../lib/communicationsAPI';
import { RichBody } from './RichBody';
import { AttachmentList } from './Attachments';

const PURIFY = { FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'input', 'object', 'embed'], FORBID_ATTR: ['style'], ADD_ATTR: ['target'] };
const toDataUrl = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });

const buildDoc = (body, cidMap, showImages) => {
  const doc = new DOMParser().parseFromString(DOMPurify.sanitize(body || '', PURIFY), 'text/html');
  doc.querySelectorAll('img[data-cid]').forEach((img) => { const u = cidMap[img.dataset.cid]; if (u) img.setAttribute('src', u); });
  if (showImages) doc.querySelectorAll('img[data-remote-src]').forEach((img) => img.setAttribute('src', img.dataset.remoteSrc));
  doc.querySelectorAll('a').forEach((a) => { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer'); });
  const csp = `default-src 'none'; img-src data:${showImages ? ' https: http:' : ''}; style-src 'unsafe-inline'`;
  return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank">`
    + '<style>body{font:14px/1.5 system-ui,sans-serif;color:#1f2937;margin:0;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}</style>'
    + `</head><body>${doc.body.innerHTML}</body></html>`;
};

// Inbound email HTML in a sandboxed iframe (no scripts); remote images blocked until "Show images";
// inline cid: images fetched through the authorised attachment endpoint.
export const EmailBody = ({ body, format, attachments, testId }) => {
  const [showImages, setShowImages] = useState(false);
  const [cidMap, setCidMap] = useState({});
  const [height, setHeight] = useState(80);
  const frame = useRef(null);
  const hasRemote = format === 'html' && /data-remote-src=/.test(body || '');
  useEffect(() => {
    let alive = true;
    (attachments || []).filter((a) => a.content_id && a.scan_status === 'clean' && (body || '').includes(`data-cid="${a.content_id}"`)).forEach((a) => {
      commsFilesAPI.blob(a.id, true).then((r) => toDataUrl(r.data)).then((u) => alive && setCidMap((m) => ({ ...m, [a.content_id]: u }))).catch(() => {});
    });
    return () => { alive = false; };
  }, [body, attachments]);
  const srcDoc = useMemo(() => (format === 'html' ? buildDoc(body, cidMap, showImages) : ''), [body, format, cidMap, showImages]);
  if (format !== 'html') return <RichBody body={body} format="text" testId={testId} />;
  const fit = () => { try { setHeight(Math.min(2000, frame.current.contentDocument.body.scrollHeight + 16)); } catch { /* cross-origin */ } };
  return (
    <div data-testid={testId}>
      {hasRemote && !showImages && (
        <button type="button" onClick={() => setShowImages(true)} className="mb-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="email-show-images">
          <ImageOff className="w-3.5 h-3.5" />Remote images blocked · Show images
        </button>
      )}
      <iframe ref={frame} title="Email content" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={srcDoc}
        onLoad={fit} style={{ height }} className="w-full border-0 bg-white rounded" data-testid="email-body-frame" />
    </div>
  );
};

const addrs = (list) => (list || []).map((a) => a.name ? `${a.name} <${a.address}>` : a.address).join(', ');

export const EmailCard = ({ m }) => {
  const out = m.direction === 'outbound';
  const visible = (m.attachments || []).filter((a) => !(a.is_inline && a.content_id));
  return (
    <div className={cn('rounded-lg border bg-card px-4 py-3 shadow-sm', out ? 'border-l-4 border-l-primary ml-8' : 'border-l-4 border-l-cyan-500 mr-8')} data-testid={`comms-email-${m.id}`}>
      <div className="flex items-start gap-2 text-xs">
        <Mail className="w-3.5 h-3.5 mt-0.5 text-cyan-600 shrink-0" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p><span className="text-muted-foreground">From </span><span className="font-semibold">{m.from?.name || m.sender_name}</span> <span className="text-muted-foreground">&lt;{m.from?.address || m.sender_address}&gt;</span></p>
          {m.to?.length > 0 && <p className="truncate" data-testid={`comms-email-to-${m.id}`}><span className="text-muted-foreground">To </span>{addrs(m.to)}</p>}
          {m.cc?.length > 0 && <p className="truncate"><span className="text-muted-foreground">Cc </span>{addrs(m.cc)}</p>}
          {out && m.bcc?.length > 0 && <p className="truncate"><span className="text-muted-foreground">Bcc </span>{addrs(m.bcc)}</p>}
        </div>
        <span className="shrink-0 text-muted-foreground">{fmtFull(m.sent_at)}</span>
      </div>
      {m.subject && <p className="text-sm font-medium mt-2">{m.subject}</p>}
      <div className="mt-2"><EmailBody body={m.body} format={m.body_format} attachments={m.attachments} testId={`comms-message-body-${m.id}`} /></div>
      <AttachmentList items={visible} staff />
    </div>
  );
};
